import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import cn from 'classnames';
import throttle from 'lodash.throttle';
import { useTranslation } from 'next-i18next';
import { usePdp } from '@context/PdpContext';
import usePrice from '@hooks/usePrice';
import { BASE64_BLUR_DATA } from '@utils/constants';
import { buildPdpImageGalleryOrder } from '@utils/buildPdpImageGalleryOrder';
import AddToCartCTA from '_components/Product/AddToCartCTA';
import PriceContainer from '_components/Product/PriceContainer';

import styles from './StickyAddToCartBar.module.scss';

interface StickyAddToCartBarProps {
  isVisible: boolean;
  prismicDealsBannerTitles?: string[];
}

const StickyAddToCartBar = ({
  isVisible,
  prismicDealsBannerTitles = [],
}: StickyAddToCartBarProps) => {
  const { t } = useTranslation('pdp');
  const { name, selectedVariant, selectedColour, variants, images, price } =
    usePdp();
  const formatPrice = usePrice();
  const barRef = useRef<HTMLDivElement | null>(null);

  // `document` exists during client hydration too, so gating the portal on
  // `typeof document !== 'undefined'` alone renders inline on the server but portalled on
  // the client's first render — a structural mismatch React can't reconcile. Mirrors
  // components/global/Modal/index.tsx: stay ungated (null) through the first render on
  // both server and client, and only portal once mounted.
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const { body, documentElement } = document;
    const defaultBottomPadding = body.style.paddingBottom;

    const clearOffsets = () => {
      body.style.paddingBottom = defaultBottomPadding;
      documentElement.style.removeProperty('--sticky-atc-bar-offset');
    };

    if (!isVisible) {
      clearOffsets();
      return clearOffsets;
    }

    const updateOffsets = () => {
      const barHeight = barRef.current?.getBoundingClientRect().height ?? 0;
      // Reserve footer space so the sticky bar does not cover content at page bottom.
      body.style.paddingBottom = `${barHeight}px`;
      // The full `bottom` value other fixed-position UI (e.g. the live-chat launcher)
      // should use to clear the bar: the bar's own resting margin (15px, matching
      // .stickyBar's `bottom`) + its real height + the same margin again as a breathing
      // gap above it, rather than sitting flush against it.
      documentElement.style.setProperty(
        '--sticky-atc-bar-offset',
        `${15 + barHeight + 15}px`
      );
    };

    const throttledUpdateOffsets = throttle(updateOffsets, 100);

    updateOffsets();
    window.addEventListener('resize', throttledUpdateOffsets);

    return () => {
      window.removeEventListener('resize', throttledUpdateOffsets);
      throttledUpdateOffsets.cancel();
      clearOffsets();
    };
  }, [isVisible]);

  // The variant may not be fully resolved yet (e.g. size not chosen) — fall back to
  // any same-colour variant, then any variant, so the bar still shows a thumbnail
  // while prompting the shopper to select a size.
  const representativeVariant =
    selectedVariant ??
    variants?.find(
      (variant) =>
        (!selectedColour || variant.colourId === selectedColour.id) &&
        variant.defaultImage
    ) ??
    variants?.find((variant) => variant.defaultImage);

  // Resolve the same hero image the PDP ImageGallery shows for the selected colour
  // (rather than the variant's independent BigCommerce `defaultImage` catalog field)
  // so the two never drift apart — `defaultImage` is admin-set per SKU and isn't
  // guaranteed to match whatever photography is tagged as the gallery's PRI image.
  const galleryHeroImage = useMemo(
    () => buildPdpImageGalleryOrder(images, selectedColour?.name ?? null)[0],
    [images, selectedColour?.name]
  );

  const thumbnailSrc =
    galleryHeroImage?.urlOriginal ??
    representativeVariant?.defaultImage?.thumbnail ??
    representativeVariant?.defaultImage?.urlOriginal;

  const currentPriceValue = price?.price?.value ?? price?.lowPrice?.value;
  const originalPriceValue =
    price?.basePrice?.value ?? price?.baseLowPrice?.value;
  const priceString = formatPrice(currentPriceValue);
  const hasOriginalPrice =
    typeof originalPriceValue === 'number' &&
    typeof currentPriceValue === 'number' &&
    originalPriceValue > currentPriceValue;
  const originalPriceString = hasOriginalPrice
    ? formatPrice(originalPriceValue)
    : null;

  if (!name) {
    return null;
  }

  const bar = (
    <div
      ref={barRef}
      role="region"
      aria-label={t('product.stickyAddToCart.ariaLabel')}
      aria-hidden={!isVisible}
      className={cn(styles.stickyBar, { [styles.stickyBarVisible]: isVisible })}
    >
      <div className={styles.stickyBarInner}>
        <div className={styles.productInfo}>
          {thumbnailSrc && (
            <div className={styles.thumbnail}>
              <Image
                src={thumbnailSrc}
                alt={
                  galleryHeroImage?.altText ||
                  representativeVariant?.defaultImage?.altText ||
                  name
                }
                width={48}
                height={48}
                className={styles.thumbnailImage}
                blurDataURL={BASE64_BLUR_DATA}
                placeholder="blur"
                sizes="96px"
              />
            </div>
          )}
          <p
            className={styles.productName}
            data-testid="sticky-atc-product-name"
          >
            {name}
          </p>
        </div>
        <div className={styles.priceAndCta}>
          <div className={styles.priceBlock}>
            <PriceContainer
              price={priceString}
              originalPrice={originalPriceString}
            />
          </div>
          <div className={styles.ctaContainer}>
            <AddToCartCTA prismicDealsBannerTitles={prismicDealsBannerTitles} />
          </div>
        </div>
      </div>
    </div>
  );

  // Portalled to `document.body` (same pattern as PromoBar's fixed overlay): this renders
  // inside the redesigned PDP's `position: sticky` right column, and Chromium traps a
  // `position: fixed` descendant's paint order inside a sticky ancestor's local stacking
  // context — no z-index value escapes it. Portalling out is what actually fixes that,
  // rather than raising z-index further.
  return isMounted ? createPortal(bar, document.body) : null;
};

export default StickyAddToCartBar;
